import BreadcrumbsTsoPq from '@components/Breadcrumbs/BreadcrumbsTsoPq';
import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import FormFieldTsoPq from '@components/Form/FormFieldTsoPq';
import FormTsoPq from '@components/Form/FormTsoPq';
import ModalDialogTsoPq from '@components/ModalDialog/ModalDialogTsoPq';
import PermissionGate from '@components/PermissionGate/PermissionGate';
import { userOrSupportRestriction } from '@components/PermissionGate/RolePermissionConfig';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  ApiCommonExternalDtoMeasurementPointExternalDto,
  useGetMeasurementPointByIdHasDataForCurrentVersionQuery,
  useGetMeasurementPointByIdHasDataQuery,
  useGetMeasurementPointByIdIsReferencedQuery,
  useGetMeasurementPointByIdQuery,
  useGetMeasurementPointGetPastVersionsByTechnicalNameQuery,
  usePostMeasurementPointMutation,
  usePostMeasurementPointNewVersionByMeasurementPointTechnicalNameMutation,
  usePutMeasurementPointMutation,
} from '@redux/apiSlices/measurementPointApi';
import { useGetTransducerModelGetAllQuery } from '@redux/apiSlices/transducerModelApi';
import { useGetVoltageLevelGetAllQuery } from '@redux/apiSlices/voltageLevelApi';
import { useAppDispatch } from '@redux/hooks';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { useRoleCheck } from '@utils/hooks/useRoleCheck';
import {
  baseOption,
  commentSchema,
  invalidActiveToDateMessage,
  mandatory,
  pastDateTime,
  stringSchema,
} from '@utils/validationSchema';
import { format, parseISO } from 'date-fns';
import dayjs from 'dayjs';
import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { object, z } from 'zod';
import {
  StyledHeader,
  StyledLineWrapper,
  StyledMeasurmentHistoryContainer,
  StyledMeasurmentPointTitle,
  StyledMeasurmentTitle,
  StyledMeasurmentValue,
  StyledMeasurmentValueLabel,
  StyledMesurments,
  StyledPageMeasurementWrapper,
  StyledSubtitleSection,
  StyledTitleSection,
  StyledTitleWrapper,
  StyledValuesWrapper,
  StyledVersionSection,
  StyledWrapper,
} from './styledManagementSections';

type MeasurementPointProps = {
  newMeasurementPoint?: boolean;
  handleDiscard?: Dispatch<SetStateAction<boolean>>;
  treePath: { id: string; entityId: number; label: string }[] | undefined;
  refetch: any;
  onDeleteConfirm?: () => void;
  onCancel?: () => void;
  deleteTitle?: string;
  deleteText?: string;
  onDiscard?: () => void;
};

const FormContent = ({
  register,
  watch,
  setValue,
  errors,
  hasDataForCurrentVersion,
  hasDataForAllEntities,
}: {
  register: any;
  watch: any;
  setValue: any;
  errors: any;
  hasDataForCurrentVersion?: boolean;
  hasDataForAllEntities?: boolean;
}): ReactNode => {
  const { t } = useTranslation();
  const { data: transducerModels } = useGetTransducerModelGetAllQuery();
  const supportOrUserAllowed = useRoleCheck(userOrSupportRestriction);

  const transducerModelsVoltageOptions = useMemo(() => {
    return (
      transducerModels?.externalDtos
        ?.filter((x) => x.type != 1)
        .map((x) => ({
          id: x.id as number,
          label: `${x.manufacturer} ${x.model}`,
          value: x.id as number,
        })) ?? []
    );
  }, [transducerModels]);
  const transducerModelsCurrentOptions = useMemo(() => {
    return (
      transducerModels?.externalDtos
        ?.filter((x) => x.type != 0)
        .map((x) => ({
          id: x.id as number,
          label: `${x.manufacturer} ${x.model}`,
          value: x.id as number,
        })) ?? []
    );
  }, [transducerModels]);
  const { data: voltageLevels } = useGetVoltageLevelGetAllQuery();

  return (
    <StyledWrapper>
      <FormFieldTsoPq
        label="management.measurementPoint.designation"
        placeholder="management.measurementPoint.designation"
        name="name"
        type="text"
        customWidth="22.125rem"
        errorMessage="general.error.characterLimit"
        required
        register={register}
        errors={errors}
        disabled={!supportOrUserAllowed}
      />
      <StyledLineWrapper>
        <FormFieldTsoPq
          label="management.measurementPoint.activFrom"
          placeholder="management.measurementPoint.activFrom"
          name="activeFrom"
          type="datetimepicker"
          errorMessage="general.error.dateSelect"
          required
          customWidth="10.5rem"
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          disabled={!supportOrUserAllowed}
        />
        <FormFieldTsoPq
          label="management.measurementPoint.activeUntil"
          placeholder="management.measurementPoint.activeUntil"
          name="activeTo"
          type="datetimepicker"
          errorMessage="general.error.dateSelect"
          customWidth="10.5rem"
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          minDateTime={dayjs(watch('activeFrom'))}
          disabled={!supportOrUserAllowed}
        />
      </StyledLineWrapper>
      <StyledLineWrapper>
        <FormFieldTsoPq
          label="management.measurementPoint.converterTypeVoltage"
          placeholder="management.measurementPoint.converterTypeVoltage"
          labelDetail="L1"
          name="transducerModelVoltage1Id"
          type="singleSelect"
          errorMessage="general.error.valueSelect"
          customWidth="10.5rem"
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          options={transducerModelsVoltageOptions}
          disabled={!supportOrUserAllowed || hasDataForCurrentVersion}
          required
        />
        <FormFieldTsoPq
          label="management.measurementPoint.converterTypeVoltage"
          placeholder="management.measurementPoint.converterTypeVoltage"
          labelDetail="L2"
          name="transducerModelVoltage2Id"
          type="singleSelect"
          errorMessage="general.error.valueSelect"
          customWidth="10.5rem"
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          options={transducerModelsVoltageOptions}
          disabled={!supportOrUserAllowed || hasDataForCurrentVersion}
          required
        />
        <FormFieldTsoPq
          label="management.measurementPoint.converterTypeVoltage"
          placeholder="management.measurementPoint.converterTypeVoltage"
          labelDetail="L3"
          name="transducerModelVoltage3Id"
          type="singleSelect"
          errorMessage="general.error.valueSelect"
          customWidth="10.5rem"
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          options={transducerModelsVoltageOptions}
          disabled={!supportOrUserAllowed || hasDataForCurrentVersion}
          required
        />
      </StyledLineWrapper>
      <StyledLineWrapper>
        <FormFieldTsoPq
          label="management.measurementPoint.converterTypeCurrent"
          placeholder="management.measurementPoint.converterTypeCurrent"
          labelDetail="L1"
          name="transducerModelCurrent1Id"
          type="singleSelect"
          errorMessage="general.error.valueSelect"
          customWidth="10.5rem"
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          disabled={!supportOrUserAllowed || hasDataForCurrentVersion}
          options={transducerModelsCurrentOptions}
        />
        <FormFieldTsoPq
          label="management.measurementPoint.converterTypeCurrent"
          placeholder="management.measurementPoint.converterTypeCurrent"
          labelDetail="L2"
          name="transducerModelCurrent2Id"
          type="singleSelect"
          errorMessage="general.error.valueSelect"
          customWidth="10.5rem"
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          disabled={!supportOrUserAllowed || hasDataForCurrentVersion}
          options={transducerModelsCurrentOptions}
        />
        <FormFieldTsoPq
          label="management.measurementPoint.converterTypeCurrent"
          placeholder="management.measurementPoint.converterTypeCurrent"
          labelDetail="L3"
          name="transducerModelCurrent3Id"
          type="singleSelect"
          errorMessage="general.error.valueSelect"
          customWidth="10.5rem"
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          disabled={!supportOrUserAllowed || hasDataForCurrentVersion}
          options={transducerModelsCurrentOptions}
        />
      </StyledLineWrapper>
      <FormFieldTsoPq
        label="management.measurementPoint.voltageLevel"
        placeholder="management.measurementPoint.voltageLevel"
        name="voltageLevelId"
        type="singleSelect"
        customWidth="22.125rem"
        errorMessage="general.error.valueSelect"
        register={register}
        watch={watch}
        setValue={setValue}
        errors={errors}
        options={
          voltageLevels?.externalDtos?.map((x) => ({
            id: x.id as number,
            label: x.name,
            value: x.id as number,
          })) ?? []
        }
        disabled={!supportOrUserAllowed || hasDataForAllEntities}
        required
      />
      <FormFieldTsoPq
        label="management.measurementPoint.connectionPointType"
        placeholder="management.measurementPoint.connectionPointType"
        name="connectionPoint"
        type="singleSelect"
        customWidth="22.125rem"
        errorMessage="general.error.valueSelect"
        register={register}
        watch={watch}
        setValue={setValue}
        errors={errors}
        options={[
          {
            id: 0,
            label: t('management.measurementPoint.connectionPointType.transformer'),
            value: 0,
          },
          {
            id: 1,
            label: t('management.measurementPoint.connectionPointType.powerline'),
            value: 1,
          },
          {
            id: 2,
            label: t('management.measurementPoint.connectionPointType.plant'),
            value: 2,
          },
        ]}
        required
        disabled={!supportOrUserAllowed}
      />
      <FormFieldTsoPq
        label="management.measurementPoint.comment"
        placeholder="management.measurementPoint.comment"
        name="comment"
        type="text"
        multiline
        rows={4}
        customWidth="47.125rem"
        errorMessage="general.error.characterLimit"
        register={register}
        errors={errors}
        disabled={!supportOrUserAllowed}
      />
    </StyledWrapper>
  );
};

const MeasurementPointHistory = ({ history }: { history: ApiCommonExternalDtoMeasurementPointExternalDto[] }) => {
  const { t } = useTranslation();

  const { data: transducerModels } = useGetTransducerModelGetAllQuery();
  const { data: voltageLevels } = useGetVoltageLevelGetAllQuery();

  const displayTransducerModelsVoltage = (measurementPointVersion: ApiCommonExternalDtoMeasurementPointExternalDto) => {
    if (
      measurementPointVersion.transducerModelVoltage1Id == measurementPointVersion.transducerModelVoltage2Id &&
      measurementPointVersion.transducerModelVoltage1Id == measurementPointVersion.transducerModelVoltage3Id
    ) {
      const transducerModelVoltage = transducerModels?.externalDtos?.find(
        (t) => t.id == measurementPointVersion.transducerModelVoltage1Id
      );
      return `${transducerModelVoltage?.manufacturer} ${transducerModelVoltage?.model}`;
    } else {
      const transducerModelVoltage1 = transducerModels?.externalDtos?.find(
        (t) => t.id == measurementPointVersion.transducerModelVoltage1Id
      );
      const transducerModelVoltage2 = transducerModels?.externalDtos?.find(
        (t) => t.id == measurementPointVersion.transducerModelVoltage2Id
      );
      const transducerModelVoltage3 = transducerModels?.externalDtos?.find(
        (t) => t.id == measurementPointVersion.transducerModelVoltage3Id
      );

      return `L1: ${transducerModelVoltage1?.manufacturer} ${transducerModelVoltage1?.model}, L2: ${transducerModelVoltage2?.manufacturer} ${transducerModelVoltage2?.model}, L3: ${transducerModelVoltage3?.manufacturer} ${transducerModelVoltage3?.model}`;
    }
  };

  const displayTransducerModelsCurrent = (measurementPointVersion: ApiCommonExternalDtoMeasurementPointExternalDto) => {
    const { transducerModelCurrent1Id, transducerModelCurrent2Id, transducerModelCurrent3Id } = measurementPointVersion;

    if (!transducerModelCurrent1Id && !transducerModelCurrent2Id && !transducerModelCurrent3Id) {
      return t('management.measurementPoint.transducerModelNotAvailable');
    }

    if (
      transducerModelCurrent1Id === transducerModelCurrent2Id &&
      transducerModelCurrent1Id === transducerModelCurrent3Id
    ) {
      const model = transducerModels?.externalDtos?.find((t) => t.id === transducerModelCurrent1Id);
      return `${model?.manufacturer ?? ''} ${model?.model ?? ''}`;
    }
    const model1 = transducerModels?.externalDtos?.find((t) => t.id === transducerModelCurrent1Id);
    const model2 = transducerModels?.externalDtos?.find((t) => t.id === transducerModelCurrent2Id);
    const model3 = transducerModels?.externalDtos?.find((t) => t.id === transducerModelCurrent3Id);

    return `L1: ${model1?.manufacturer ?? ''} ${model1?.model ?? ''}, L2: ${model2?.manufacturer ?? ''} ${model2?.model ?? ''}, L3: ${model3?.manufacturer ?? ''} ${model3?.model ?? ''}`;
  };

  const getConnectionPointType = (connectionPointType: number) => {
    switch (connectionPointType) {
      case 0:
        return t('management.measurementPoint.connectionPointType.transformer');
      case 1:
        return t('management.measurementPoint.connectionPointType.powerline');
      default:
        return '';
    }
  };

  return (
    <>
      {history.map((measurementPointVersion) => (
        <StyledMeasurmentHistoryContainer key={measurementPointVersion.id}>
          <StyledMeasurmentTitle>{measurementPointVersion.name}</StyledMeasurmentTitle>
          <StyledValuesWrapper>
            <StyledLineWrapper>
              <StyledMeasurmentValueLabel>{t('management.measurementPoint.active')}</StyledMeasurmentValueLabel>
              <StyledMeasurmentValue>
                {measurementPointVersion.activeFrom && measurementPointVersion.activeTo
                  ? `${format(parseISO(measurementPointVersion.activeFrom), 'dd.MM.yyyy HH:mm')} - ${format(parseISO(measurementPointVersion.activeTo), 'dd.MM.yyyy HH:mm')}`
                  : ''}
              </StyledMeasurmentValue>
            </StyledLineWrapper>
            <StyledLineWrapper>
              <StyledMeasurmentValueLabel>
                {t('management.measurementPoint.converterTypeVoltage')}
              </StyledMeasurmentValueLabel>
              <StyledMeasurmentValue>{displayTransducerModelsVoltage(measurementPointVersion)}</StyledMeasurmentValue>
            </StyledLineWrapper>
            <StyledLineWrapper>
              <StyledMeasurmentValueLabel>
                {t('management.measurementPoint.converterTypeCurrent')}
              </StyledMeasurmentValueLabel>
              <StyledMeasurmentValue>{displayTransducerModelsCurrent(measurementPointVersion)}</StyledMeasurmentValue>
            </StyledLineWrapper>
            <StyledLineWrapper>
              <StyledMeasurmentValueLabel>{t('management.measurementPoint.voltageLevel')}</StyledMeasurmentValueLabel>
              <StyledMeasurmentValue>
                {voltageLevels?.externalDtos?.find((x) => x.id == measurementPointVersion.voltageLevelId)?.name}
              </StyledMeasurmentValue>
            </StyledLineWrapper>
            <StyledLineWrapper>
              <StyledMeasurmentValueLabel>
                {t('management.measurementPoint.connectionPointType')}
              </StyledMeasurmentValueLabel>
              <StyledMeasurmentValue>
                {getConnectionPointType(measurementPointVersion.connectionPoint)}
              </StyledMeasurmentValue>
            </StyledLineWrapper>
            <StyledLineWrapper>
              <StyledMeasurmentValueLabel>{t('management.measurementPoint.comment')}</StyledMeasurmentValueLabel>
              <StyledMeasurmentValue>{measurementPointVersion.comment}</StyledMeasurmentValue>
            </StyledLineWrapper>
          </StyledValuesWrapper>
        </StyledMeasurmentHistoryContainer>
      ))}
    </>
  );
};

const MeasurementPoint = ({
  newMeasurementPoint,
  handleDiscard,
  treePath,
  refetch,
  onDeleteConfirm,
  onCancel,
  deleteTitle,
  deleteText,
  onDiscard,
}: MeasurementPointProps) => {
  const dispatch = useAppDispatch();

  const { type, deviceId } = useParams();
  const { t } = useTranslation();
  const supportOrUserAllowed = useRoleCheck(userOrSupportRestriction);

  const { data: measurementPoint } = useGetMeasurementPointByIdQuery(
    type == 'messpunkt' && deviceId ? Number(deviceId) : skipToken
  );
  const { data: history } = useGetMeasurementPointGetPastVersionsByTechnicalNameQuery(
    measurementPoint?.externalDto?.technicalName ?? skipToken
  );
  const { data: hasDataForCurrentVersion } = useGetMeasurementPointByIdHasDataForCurrentVersionQuery(
    type == 'messpunkt' && deviceId ? Number(deviceId) : skipToken
  );

  const { data: hasDataForAnyVersion } = useGetMeasurementPointByIdHasDataQuery(
    type == 'messpunkt' && deviceId ? Number(deviceId) : skipToken
  );
  const { data: isReferenced } = useGetMeasurementPointByIdIsReferencedQuery(
    type == 'messpunkt' && deviceId ? Number(deviceId) : skipToken
  );

  const [addEntity] = usePostMeasurementPointMutation();
  const [editEntity] = usePutMeasurementPointMutation();
  const [createNewVersionMeasuringPoint] = usePostMeasurementPointNewVersionByMeasurementPointTechnicalNameMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const activeTo = measurementPoint?.externalDto?.activeTo;
  const isDisabled = !activeTo || new Date(activeTo).getTime() > Date.now();

  const handleFormSubmit = async (data: any) => {
    try {
      if (newMeasurementPoint) {
        await addEntity({
          externalDto: {
            tsoId: 1,
            substationId: treePath?.findLast((x) => true)?.entityId,
            ...data,
          },
        }).unwrap();

        await refetch();

        dispatch(
          setSnackbar({
            open: true,
            type: 'success',
            textContent: t('snackbar.measurementPoint.new.success'),
            copyToClipboard: false,
            requestID: undefined,
          })
        );
      } else {
        await editEntity({ externalDto: { ...measurementPoint?.externalDto, ...data } }).unwrap();

        await refetch();

        dispatch(
          setSnackbar({
            open: true,
            type: 'success',
            textContent: t('snackbar.measurementPoint.update.success'),
            copyToClipboard: false,
            requestID: undefined,
          })
        );
      }

      handleDiscard && handleDiscard(false);
    } catch (error: any) {
      console.error('Error submitting measurement point:', error);
      const requestID = error?.data?.traceId || undefined;

      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: newMeasurementPoint
            ? t('snackbar.measurementPoint.new.error')
            : t('snackbar.measurementPoint.update.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const handleCreateNewMeasurementPointVersion = async () => {
    try {
      if (!measurementPoint?.externalDto?.technicalName) return;

      await createNewVersionMeasuringPoint(measurementPoint.externalDto.technicalName).unwrap();
      await refetch();

      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.measurementPoint.newVersion.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );
    } catch (error: any) {
      console.error('Error creating new version of measurement point:', error);
      const requestID = error?.data?.traceId || undefined;

      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.measurementPoint.newVersion.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const validationSchema = object({
    name: mandatory.and(stringSchema),
    activeFrom: mandatory.and(pastDateTime),
    activeTo: z.preprocess((val) => (val === '' || val == null ? undefined : val), pastDateTime.optional()),
    transducerModelVoltage1Id: mandatory,
    transducerModelVoltage2Id: mandatory,
    transducerModelVoltage3Id: mandatory,
    transducerModelCurrent1Id: baseOption.optional(),
    transducerModelCurrent2Id: baseOption.optional(),
    transducerModelCurrent3Id: baseOption.optional(),
    voltageLevelId: mandatory,
    connectionPoint: mandatory,
    comment: commentSchema,
  }).refine(
    ({ activeFrom, activeTo }) => {
      if (!activeFrom || !activeTo) return true;
      return new Date(activeTo) > new Date(activeFrom);
    },
    {
      path: ['activeTo'],
      error: invalidActiveToDateMessage,
    }
  );

  return (
    (deviceId || newMeasurementPoint) && (
      <StyledPageMeasurementWrapper newMeasurementPoint={newMeasurementPoint}>
        <StyledHeader>
          <StyledTitleWrapper>
            <StyledTitleSection>
              <StyledMeasurmentPointTitle>
                {(newMeasurementPoint && t('management.measurementPoint.create.title')) ||
                  measurementPoint?.externalDto?.name}
              </StyledMeasurmentPointTitle>
              {!newMeasurementPoint && (
                <PermissionGate roles={userOrSupportRestriction}>
                  <IconButtonTsoPq
                    sizeVariant={'l'}
                    tooltip={
                      isReferenced
                        ? t('management.measurementPoint.measurementPointCannotDelete')
                        : t('management.measurementPoint.measurementPointDeleteTitle')
                    }
                    buttonType="transparentPrimaryNoEffect"
                    disabled={isReferenced}
                    icon={faTrash}
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowDeleteModal(true);
                    }}
                  />
                </PermissionGate>
              )}
            </StyledTitleSection>
            <StyledVersionSection>
              {!newMeasurementPoint && (
                <PermissionGate roles={userOrSupportRestriction}>
                  <ButtonTsoPq
                    size="medium"
                    buttonType="filledPrimary"
                    disabled={isDisabled}
                    textContent={t('version.createNewVersion')}
                    tooltip={t('version.createNewVersion.tooltip')}
                    onClick={handleCreateNewMeasurementPointVersion}
                  />
                </PermissionGate>
              )}
            </StyledVersionSection>
          </StyledTitleWrapper>
          {!newMeasurementPoint && (
            <StyledSubtitleSection>{t('management.measurementPoint.subtitle')}</StyledSubtitleSection>
          )}
          <BreadcrumbsTsoPq breadcrumbs={treePath} disableLinks={newMeasurementPoint} />
        </StyledHeader>
        <FormTsoPq
          newForm={newMeasurementPoint}
          defaultValues={{}}
          onSubmit={handleFormSubmit}
          discardChanges={handleDiscard}
          customProps={history?.externalDtos ?? []}
          values={newMeasurementPoint ? {} : (measurementPoint?.externalDto ?? {})}
          schema={validationSchema}
          withoutDiscard={!supportOrUserAllowed}
          hideSaveButton={!supportOrUserAllowed}
        >
          {(formProps) => (
            <FormContent
              {...formProps}
              hasDataForCurrentVersion={hasDataForCurrentVersion && !newMeasurementPoint}
              hasDataForAllEntities={hasDataForAnyVersion && !newMeasurementPoint}
            />
          )}
        </FormTsoPq>
        <StyledMesurments>
          {!newMeasurementPoint && (history?.externalDtos?.length ?? 0) > 0 && (
            <>
              <StyledMeasurmentPointTitle>{t('management.measurementPoint.history.title')}</StyledMeasurmentPointTitle>
              <MeasurementPointHistory history={history?.externalDtos ?? []} />
            </>
          )}
        </StyledMesurments>
        {showDeleteModal && (
          <ModalDialogTsoPq
            modalState={showDeleteModal}
            title={deleteTitle ?? ''}
            deleteConfirmationModal
            confirmationText={deleteText ?? ''}
            onConfirm={() => {
              onDeleteConfirm?.();
              setShowDeleteModal(false);
              onDiscard?.();
            }}
            onCancel={() => {
              onCancel?.();
              setShowDeleteModal(false);
            }}
            onClose={() => setShowDeleteModal(false)}
          />
        )}
      </StyledPageMeasurementWrapper>
    )
  );
};

export default MeasurementPoint;
